import React, { useState, useEffect, useRef } from 'react';
import { X, MessageSquare, Check, BookOpen, Monitor } from 'lucide-react';
import './FeedbackButton.css';

const VIEW_LABELS = {
  portal:       { label: 'My Learning (Home)',  type: 'page' },
  catalogue:    { label: 'Course Catalogue',    type: 'page' },
  certificates: { label: 'Certificates',        type: 'page' },
  profile:      { label: 'My Profile',          type: 'page' },
};

export default function FeedbackButton({ user, view, selectedCourse }) {
  const [open,        setOpen]        = useState(false);
  const [message,     setMessage]     = useState('');
  const [sending,     setSending]     = useState(false);
  const [error,setError]=useState('');
  const requestId=useRef(crypto.randomUUID());
  const [sent,        setSent]        = useState(false);
  const [courseTitle, setCourseTitle] = useState('');
  const triggerRef = useRef(null);
  const wasOpen = useRef(false);

  // Return focus to the trigger when the panel closes.
  useEffect(() => {
    if (wasOpen.current && !open && triggerRef.current) triggerRef.current.focus();
    wasOpen.current = open;
  }, [open]);

  useEffect(() => {
    if (view === 'lesson' && selectedCourse) {
      fetch(`/api/courses/${selectedCourse}`)
        .then(r => r.json())
        .then(j => setCourseTitle(j.data?.title || selectedCourse))
        .catch(() => setCourseTitle(selectedCourse));
    } else {
      setCourseTitle('');
    }
  }, [view, selectedCourse]);

  const context = view === 'lesson' && selectedCourse
    ? { type: 'course', id: selectedCourse, label: courseTitle || selectedCourse }
    : { type: 'page',   id: view, label: VIEW_LABELS[view]?.label || view };

  async function submit(e) {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true); setError('');
    try {
      const response=await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId:       user.id,
          userName:     user.name,
          contextType:  context.type,
          contextId:    context.id,
          contextLabel: context.label,
          message,requestId:requestId.current,
        }),
      });
      const result=await response.json();if(!response.ok||!result.success)throw Error(result.error||'Request could not be saved.');
      requestId.current=crypto.randomUUID();
      setSent(true);
      setMessage('');
      setTimeout(() => { setSent(false); setOpen(false); }, 2500);
    } catch (e) {setError(e.message||'Could not save your request. Your draft is retained.');}
    setSending(false);
  }

  return (
    <>
      <button type="button" ref={triggerRef} className={`fb-trigger ${open ? 'fb-trigger--open' : ''}`} onClick={() => setOpen(o => !o)} title="Send feedback" aria-label={open ? 'Close feedback form' : 'Send feedback'} aria-expanded={open} aria-haspopup="dialog">
        <span className="fb-trigger-icon" aria-hidden="true">{open ? <X size={18} strokeWidth={1.75} /> : <MessageSquare size={18} strokeWidth={1.75} />}</span>
        {!open && <span className="fb-trigger-label">Feedback</span>}
      </button>

      {open && (
        <div className="fb-modal" role="dialog" aria-labelledby="fb-modal-title" onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } }}>
          <div className="fb-modal-header">
            <span className="fb-modal-title" id="fb-modal-title">Send Feedback</span>
            <button type="button" className="fb-close" onClick={() => setOpen(false)} aria-label="Close feedback form"><X size={16} strokeWidth={1.75} aria-hidden="true" /></button>
          </div>

          <div className="fb-context-chip">
            <span className="fb-context-icon" aria-hidden="true">{context.type === 'course' ? <BookOpen size={14} strokeWidth={1.75} /> : <Monitor size={14} strokeWidth={1.75} />}</span>
            <span className="fb-context-text">
              <span className="fb-context-prefix">{context.type === 'course' ? 'Course: ' : 'Page: '}</span>
              <strong>{context.label}</strong>
            </span>
          </div>

          {error&&<p role="alert">{error}</p>}{sent ? (
            <div className="fb-sent" role="status">
              <span className="fb-sent-icon" aria-hidden="true"><Check size={18} strokeWidth={1.75} /></span>
              <p>Feedback sent | thank you!</p>
            </div>
          ) : (
            <form className="fb-form" onSubmit={submit}>
              <label htmlFor="fb-message" className="sr-only">Your feedback</label>
              <textarea
                id="fb-message"
                className="fb-textarea"
                rows={4}
                placeholder="What's on your mind? Bug, suggestion, or anything else…"
                value={message}
                onChange={e => setMessage(e.target.value)}
                autoFocus
              />
              <button type="submit" className="btn btn-primary fb-submit" disabled={sending || !message.trim()}>
                {sending ? 'Sending…' : 'Send Feedback'}
              </button>
            </form>
          )}
        </div>
      )}
    </>
  );
}
